import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumbs, organizationLd, webSiteLd, datasetLd } from '../seo';

interface CityPrice {
  city: string;
  slug: string;
  avg_monthly: number | null;
  count: number;
}

export default function CheapestParking() {
  const [rows, setRows] = useState<CityPrice[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string>('');

  useEffect(() => {
    fetch('/api/stats/leaderboard?sort=cheapest&min_count=1&limit=20')
      .then((r) => r.json())
      .then((j) => {
        if (Array.isArray(j?.data?.results)) setRows(j.data.results);
      })
      .catch(() => undefined);
    setUpdatedAt(new Date().toISOString());
  }, []);

  return (
    <>
      <Breadcrumbs
        items={[
          { label: 'Home', to: '/' },
          { label: 'Prices', to: '/prices' },
          { label: 'Cheapest metros' },
        ]}
        pageTitle="Cheapest Parking in India — City-by-City | Parksy"
        pageDescription="Which Indian metros have the cheapest parking? Live, crowdsourced data ranked by average monthly rate."
        extraSchemas={[
          organizationLd(),
          webSiteLd(),
          datasetLd({
            name: 'Cheapest metros for parking',
            description: 'Indian cities ranked by average monthly parking rate.',
            url: 'https://parksy.rent/cheapest-parking',
            citySlug: 'india',
            updatedAt,
          }),
        ]}
      />
      <div className="max-w-4xl mx-auto px-4 py-10">
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Cheapest metros for parking</h1>
          <p className="text-gray-600">Ranked by average monthly parking rate.</p>
        </header>

        {rows.length === 0 ? (
          <div className="bg-white border rounded-xl p-6 text-sm text-gray-500 text-center">
            Leaderboard populates as listings grow.
          </div>
        ) : (
          <div className="bg-white border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2">Rank</th>
                  <th className="text-left px-4 py-2">City</th>
                  <th className="text-right px-4 py-2">Listings</th>
                  <th className="text-right px-4 py-2">Avg ₹/month</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.slug} className="border-t">
                    <td className="px-4 py-2 text-gray-500">{idx + 1}</td>
                    <td className="px-4 py-2 font-medium text-gray-900">
                      <Link to={`/prices/${row.slug}`} className="hover:text-parksy-600">
                        {row.city}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-right text-gray-600">{row.count}</td>
                    <td className="px-4 py-2 text-right text-gray-600">
                      {row.avg_monthly ? `₹${Math.round(row.avg_monthly).toLocaleString('en-IN')}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-6 text-xs text-gray-500">
          Data is crowdsourced and may contain outliers. Parksy applies automatic filtering and
          community reports.
        </p>
      </div>
    </>
  );
}
